import { useMemo, useState } from 'react';
import { useSearch } from '@tanstack/react-router';
import { addWeeks, startOfDay } from 'date-fns';

import { useRescheduleAppointmentMutation } from '@/api/hooks/mutations/use-reschedule-appointment-mutation';
import { type AvailabilityCalendarFormData } from '@/components/AvailabilityCalendar';
import { ErrorModal } from '@/components/ErrorModal';
import {
  DEFAULT_ORG_PHONE_NUMBER,
  DEFAULT_TELEHEALTH_CONTACT_TYPE,
  NEW_PATIENT_VISIT_DURATION_IN_MINUTES,
  TELEHEALTH_CONTACT_TYPE,
} from '@/lib/constants';
import { ApiError } from '@/lib/errors';
import { logger } from '@/lib/logger';
import type { Appointment } from '@/types/order.type';

import { RescheduleAppointmentConfirmation } from './components/RescheduleAppointmentConfirmation';
import { RescheduleAppointmentPatientIdentity } from './components/RescheduleAppointmentPatientIdentity';
import type { RescheduleAppointmentPatientIdentityFormData } from './components/RescheduleAppointmentPatientIdentity';
import { RescheduleAppointmentSelectSlot } from './components/RescheduleAppointmentSelectSlot';

const RescheduleStepEnum = {
  PATIENT_IDENTITY: 'patient_identity',
  SELECT_SLOT: 'select_slot',
  CONFIRMATION: 'confirmation',
} as const;

const stepsWithAppointmentId = [RescheduleStepEnum.SELECT_SLOT, RescheduleStepEnum.CONFIRMATION];

const stepsWithoutAppointmentId = [
  RescheduleStepEnum.PATIENT_IDENTITY,
  RescheduleStepEnum.SELECT_SLOT,
  RescheduleStepEnum.CONFIRMATION,
];

const VANTAGE_APPOINTMENT_TYPE_ID: string = import.meta.env
  .VITE_HEALTHIE_APPOINTMENT_TYPE_VANTAGE_VISIT_ID;
const HEALTHIE_APPOINTMENT_TYPE_ID: string = import.meta.env
  .VITE_HEALTHIE_APPOINTMENT_TYPE_NEW_PATIENT_VISIT_ID;
const RESCHEDULE_CONTACT_TYPE = {
  [VANTAGE_APPOINTMENT_TYPE_ID]: TELEHEALTH_CONTACT_TYPE.PHONE,
  [HEALTHIE_APPOINTMENT_TYPE_ID]: DEFAULT_TELEHEALTH_CONTACT_TYPE,
};

const SCHEDULING_FLOW_DEFAULT_ERROR_MODAL: { title: string; description: string } = {
  title: 'Something went wrong',
  description: "We're having trouble reaching the scheduling page. Please try again.",
};

function getSchedulingErrorModalContent(error: unknown): {
  title: string;
  description: string;
} {
  if (error instanceof ApiError && error.status === 409) {
    return {
      title: "This appointment can't be rescheduled online",
      description:
        'Your order is not in a status that allows rescheduling. Please contact support if you need help changing your appointment or making updates to your order.',
    };
  }

  return SCHEDULING_FLOW_DEFAULT_ERROR_MODAL;
}

export const RescheduleAppointment = () => {
  const startDate = startOfDay(new Date());
  const endDate = startOfDay(addWeeks(startDate, 2));
  const searchParams = useSearch({
    from: '/appLayout/wizardLayout/appointment/reschedule',
  });
  const appointmentId = searchParams.appointmentId?.toString();
  const appointmentTypeId = searchParams.appointmentTypeId?.toString();
  const licensedInState = searchParams.licensedInState?.toString() ?? null;

  const [patientIdentification, setPatientIdentification] =
    useState<RescheduleAppointmentPatientIdentityFormData | null>(null);

  const rescheduleMutation = useRescheduleAppointmentMutation();

  const patientNeedsToIdentify = useMemo(() => {
    return !appointmentId?.length;
  }, [appointmentId]);

  const requiredSteps = useMemo(() => {
    return patientNeedsToIdentify ? stepsWithoutAppointmentId : stepsWithAppointmentId;
  }, [patientNeedsToIdentify]);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const currentStep = requiredSteps[currentStepIndex];

  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorModalContent, setErrorModalContent] = useState(SCHEDULING_FLOW_DEFAULT_ERROR_MODAL);

  const openGenericSchedulingErrorModal = () => {
    setErrorModalContent(SCHEDULING_FLOW_DEFAULT_ERROR_MODAL);
    setIsErrorModalOpen(true);
  };
  const [appointmentDetails, setAppointmentDetails] = useState<{
    appointment: Appointment;
    patient: { phoneNumber: string };
  } | null>(null);

  const handleOnSubmitPatientIdentification = (
    data: RescheduleAppointmentPatientIdentityFormData
  ) => {
    setPatientIdentification(data);
    setCurrentStepIndex(currentStepIndex + 1);
  };

  const handleOnSubmitAvailabilityCalendar = async (data: AvailabilityCalendarFormData) => {
    if (!data.selectedSlot || !data.selectedProviderId) {
      openGenericSchedulingErrorModal();
      return;
    }

    const isAppointmentIdComplete = appointmentId;
    const isPatientIdentificationComplete =
      patientIdentification?.email &&
      patientIdentification?.firstName &&
      patientIdentification?.lastName &&
      patientIdentification?.phoneNumber;

    if (!isAppointmentIdComplete && !isPatientIdentificationComplete) {
      openGenericSchedulingErrorModal();
      return;
    }

    const params = isAppointmentIdComplete
      ? { appointmentId }
      : {
          firstName: patientIdentification!.firstName,
          lastName: patientIdentification!.lastName,
          email: patientIdentification!.email,
          phoneNumber: patientIdentification!.phoneNumber,
        };

    try {
      const result = await rescheduleMutation.mutateAsync({
        appointmentTypeId: appointmentTypeId!.toString(),
        appointmentDate: data.selectedSlot,
        providerId: data.selectedProviderId,
        contactType: contactType,
        ...params,
      });

      if (!result.payload || !result.payload.date || !result.payload.provider) {
        openGenericSchedulingErrorModal();
        return;
      }

      const appointment: Appointment = {
        scheduledTime: result.payload.date ? new Date(result.payload.date) : undefined,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        provider: {
          name: result.payload.provider?.full_name ?? '',
          phoneNumber: result.payload.provider?.phone_number ?? DEFAULT_ORG_PHONE_NUMBER,
        },
        durationInMinutes: NEW_PATIENT_VISIT_DURATION_IN_MINUTES,
      };

      const patient = {
        phoneNumber: patientIdentification?.phoneNumber ?? '',
      };

      setAppointmentDetails({ appointment, patient });
      setCurrentStepIndex(currentStepIndex + 1);
    } catch (err) {
      logger.error('Failed to reschedule appointment', err);
      setErrorModalContent(getSchedulingErrorModalContent(err));
      setIsErrorModalOpen(true);
    }
  };

  const contactType = useMemo(() => {
    if (!appointmentTypeId) return DEFAULT_TELEHEALTH_CONTACT_TYPE;
    return RESCHEDULE_CONTACT_TYPE[appointmentTypeId] ?? DEFAULT_TELEHEALTH_CONTACT_TYPE;
  }, [appointmentTypeId]);

  const isPhoneCall = useMemo(() => {
    return contactType === TELEHEALTH_CONTACT_TYPE.PHONE;
  }, [contactType]);

  return (
    <>
      {currentStep === RescheduleStepEnum.PATIENT_IDENTITY && (
        <RescheduleAppointmentPatientIdentity onSubmit={handleOnSubmitPatientIdentification} />
      )}
      {currentStep === RescheduleStepEnum.SELECT_SLOT && (
        <RescheduleAppointmentSelectSlot
          startDate={startDate}
          endDate={endDate}
          licensedInState={licensedInState}
          onSubmit={handleOnSubmitAvailabilityCalendar}
          appointmentTypeId={appointmentTypeId ?? ''}
          contactType={contactType}
        />
      )}
      {currentStep === RescheduleStepEnum.CONFIRMATION && appointmentDetails && (
        <RescheduleAppointmentConfirmation
          appointment={appointmentDetails.appointment}
          isPhoneCall={isPhoneCall}
        />
      )}
      {isErrorModalOpen && (
        <ErrorModal
          isOpen={isErrorModalOpen}
          setIsOpen={setIsErrorModalOpen}
          title={errorModalContent.title}
          description={errorModalContent.description}
        />
      )}
    </>
  );
};
